'use client';

import { useCallback, useEffect, useState } from 'react';

import { authentication } from '@microsoft/teams-js';
import { Button } from '@onyx/ui/components/colleague/button';
import { useRouter } from 'next/navigation';
// eslint-disable-next-line @typescript-eslint/no-restricted-imports
import { signIn as nextAuthSignIn } from 'next-auth/react';

type Props = {
  callbackUrl: string;
};

const isSafeInternalPath = (url: string): boolean =>
  url.startsWith('/') && !url.startsWith('//') && !url.startsWith('/\\');

export const TeamsSignIn = ({ callbackUrl }: Props) => {
  const router = useRouter();
  const [error, setError] = useState<string | null>(null);

  const safeCallbackUrl = isSafeInternalPath(callbackUrl) ? callbackUrl : '/';

  const authenticate = useCallback(async () => {
    /* eslint-disable-next-line functional/no-try-statement */
    try {
      const teamsToken = await authentication.getAuthToken();

      const result = await nextAuthSignIn('teams', {
        teamsToken,
        redirect: false,
        callbackUrl: safeCallbackUrl,
      });

      if (!result || result.error) {
        setError('Unable to sign in. Please try again.');
        return;
      }

      router.push(safeCallbackUrl);
    } catch {
      setError('Unable to sign in via Teams. Please try again.');
    }
  }, [safeCallbackUrl, router]);

  useEffect(() => {
    void authenticate();
  }, [authenticate]);

  if (error) {
    /* istanbul ignore next -- SSR guard; window is always defined in jsdom */
    const browserUrl =
      typeof window === 'undefined'
        ? safeCallbackUrl
        : `${window.location.origin}${safeCallbackUrl}`;

    return (
      <div>
        <p>{error}</p>
        <Button
          onClick={() => {
            setError(null);
            void authenticate();
          }}
        >
          Retry
        </Button>
        <p>
          <a href={browserUrl} target="_blank" rel="noopener noreferrer">
            Open Help in the browser
          </a>
        </p>
      </div>
    );
  }

  return <p>Signing in…</p>;
};
